import type { CSSProperties, ReactNode, ReactPortal } from 'react';
import styled from '@emotion/styled';
import { computePosition } from '@floating-ui/dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SuggestionPopoverContainer = styled.div`
  position: absolute;
`;

type SuggestionPopoverProps = {
  children: ReactNode;
  portalTarget: Element;
  target: Element | null;
  width?: string;
  zIndex?: number;
};

export const SuggestionPopover = (
  props: SuggestionPopoverProps
): ReactPortal => {
  const {
    children,
    portalTarget,
    target,
    width = 'max-content',
    zIndex,
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>({
    position: 'absolute',
    width,
    top: 0,
    left: 0,
  });

  const compute = useCallback(async () => {
    if (target && ref.current) {
      const { x, y } = await computePosition(target!, ref.current!, {
        placement: 'bottom-start',
      });

      setStyle({
        position: 'absolute',
        width,
        top: y,
        left: x,
        zIndex,
      });
    }
  }, [target, width, zIndex]);

  useEffect(() => {
    compute();
  }, [compute]);

  useEffect(() => {
    const element = portalTarget;

    if (element && element !== document.body) {
      element.addEventListener('scroll', compute, { passive: true });

      return () => {
        element.removeEventListener('scroll', compute);
      };
    }
  }, [portalTarget, compute]);

  return createPortal(
    <SuggestionPopoverContainer ref={ref} style={style}>
      {children}
    </SuggestionPopoverContainer>,
    portalTarget
  );
};
